import React from "react";
import { HATCH, MAT_HATCH, LINE, DIM_STYLE } from "../../engine/drawingStandards";

// ─── Scale helpers ─────────────────────────────────────────────────────────────
// In drawing mode: 1 world mm → (1 / scale) drawing mm → * PX_PER_MM svg pixels
export const PX_PER_MM = 3.78;  // 96dpi / 25.4 = 3.78 px/mm
export const mmToPx = mm => mm * PX_PER_MM;

// Scale a world mm value to drawing px at a given drawing scale
export const worldToPx = (worldMm, scale) => (worldMm / scale) * PX_PER_MM;

// ─── SVG Hatch pattern defs ────────────────────────────────────────────────────
export function HatchDefs({ scale }) {
  const sp = (mm) => mmToPx(mm);  // spacing in px at screen scale

  return (
    <defs>
      {/* Brick / masonry — diagonal cross-hatch */}
      <pattern id="hatch-BRICK" width={sp(4)} height={sp(4)} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2={sp(4)} stroke="#000" strokeWidth={mmToPx(0.12)} opacity="0.8"/>
      </pattern>

      {/* Concrete / RC — dot pattern + cross */}
      <pattern id="hatch-CONCRETE" width={sp(5)} height={sp(5)} patternUnits="userSpaceOnUse">
        <circle cx={sp(1.5)} cy={sp(1.5)} r={mmToPx(0.4)} fill="#333" opacity="0.7"/>
        <line x1="0" y1={sp(2.5)} x2={sp(5)} y2={sp(2.5)} stroke="#333" strokeWidth={mmToPx(0.1)} opacity="0.4"/>
      </pattern>

      {/* Partition / insulation — zigzag */}
      <pattern id="hatch-PARTITION" width={sp(6)} height={sp(4)} patternUnits="userSpaceOnUse">
        <polyline points={`0,${sp(2)} ${sp(2)},${sp(0.5)} ${sp(4)},${sp(3.5)} ${sp(6)},${sp(2)}`}
          fill="none" stroke="#555" strokeWidth={mmToPx(0.15)} opacity="0.6"/>
      </pattern>

      {/* Ground / earth — diagonal with dots */}
      <pattern id="hatch-EARTH" width={sp(6)} height={sp(6)} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2={sp(6)} stroke="#8B4513" strokeWidth={mmToPx(0.25)} opacity="0.5"/>
        <circle cx={sp(3)} cy={sp(3)} r={mmToPx(0.5)} fill="#8B4513" opacity="0.4"/>
      </pattern>

      {/* AAC / Hollow — horizontal lines */}
      <pattern id="hatch-AAC" width={sp(4)} height={sp(3)} patternUnits="userSpaceOnUse">
        <line x1="0" y1={sp(1.5)} x2={sp(4)} y2={sp(1.5)} stroke="#555" strokeWidth={mmToPx(0.12)} opacity="0.6"/>
      </pattern>

      {/* Arrow marker for dimension lines */}
      <marker id="dim-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
        <path d="M0,0 L0,6 L6,3 z" fill="#000"/>
      </marker>
      <marker id="dim-arrow-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
        <path d="M6,0 L6,6 L0,3 z" fill="#000"/>
      </marker>
      {/* Tick marker */}
      <marker id="dim-tick" markerWidth="4" markerHeight="8" refX="2" refY="4" orient="auto">
        <line x1="3" y1="0" x2="1" y2="8" stroke="#000" strokeWidth="1.2"/>
      </marker>
    </defs>
  );
}

// ─── Hatch fill for a wall rectangle ─────────────────────────────────────────
export function WallHatch({ x, y, w, h, matId, isSection = true }) {
  const hatchId = matId === "RC" ? "hatch-CONCRETE"
    : matId === "AAC" || matId === "HOLLOW" ? "hatch-AAC"
    : matId === "PARTITION" ? "hatch-PARTITION"
    : matId === "GLASS" ? null
    : "hatch-BRICK";

  return (
    <g>
      {/* Solid fill */}
      <rect x={x} y={y} width={w} height={h}
        fill={matId === "GLASS" ? "#e8f4ff" : "#ffffff"}/>
      {/* Hatch overlay */}
      {hatchId && (
        <rect x={x} y={y} width={w} height={h}
          fill={`url(#${hatchId})`} opacity={isSection ? 1 : 0.5}/>
      )}
      {/* Cut outline — heavy */}
      {isSection && (
        <rect x={x} y={y} width={w} height={h}
          fill="none" stroke="#000" strokeWidth={mmToPx(LINE.OUTLINE.weight)}/>
      )}
    </g>
  );
}

// ─── Linear dimension string ──────────────────────────────────────────────────
/**
 * Draws a single linear dimension line between two points.
 * orientation: "h" = horizontal, "v" = vertical
 * offset: perpendicular distance from the measured line
 * above: if true, dim line is above/left; if false, below/right
 */
export function DimLine({ x1, y1, x2, y2, offset = 20, text, orientation = "h", textSize = 2.5 }) {
  const ts = mmToPx(textSize);
  const extGap = mmToPx(DIM_STYLE.extensionGap);
  const extLen = mmToPx(DIM_STYLE.extensionLen);
  const off    = mmToPx(offset);
  const sw     = mmToPx(LINE.DIMENSION.weight);

  if (orientation === "h") {
    const dimY = Math.min(y1, y2) - off;
    const ey1  = y1 > dimY ? y1 - extGap : y1 + extGap;
    const ey2  = y2 > dimY ? y2 - extGap : y2 + extGap;
    return (
      <g style={{ pointerEvents: "none" }}>
        {/* Extension lines */}
        <line x1={x1} y1={ey1} x2={x1} y2={dimY - extLen} stroke="#000" strokeWidth={sw}/>
        <line x1={x2} y1={ey2} x2={x2} y2={dimY - extLen} stroke="#000" strokeWidth={sw}/>
        {/* Dimension line with ticks */}
        <line x1={x1} y1={dimY} x2={x2} y2={dimY} stroke="#000" strokeWidth={sw}
          markerStart="url(#dim-tick)" markerEnd="url(#dim-tick)"/>
        {/* Text */}
        <text x={(x1+x2)/2} y={dimY - mmToPx(1.5)} textAnchor="middle"
          fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000"
          style={{ userSelect: "none" }}>
          {text}
        </text>
      </g>
    );
  } else {
    // vertical
    const dimX = Math.min(x1, x2) - off;
    return (
      <g style={{ pointerEvents: "none" }}>
        <line x1={x1 - extGap} y1={y1} x2={dimX + extLen} y2={y1} stroke="#000" strokeWidth={sw}/>
        <line x1={x2 - extGap} y1={y2} x2={dimX + extLen} y2={y2} stroke="#000" strokeWidth={sw}/>
        <line x1={dimX} y1={y1} x2={dimX} y2={y2} stroke="#000" strokeWidth={sw}
          markerStart="url(#dim-tick)" markerEnd="url(#dim-tick)"/>
        <text x={dimX - mmToPx(2)} y={(y1+y2)/2} textAnchor="middle"
          fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000"
          transform={`rotate(-90,${dimX - mmToPx(2)},${(y1+y2)/2})`}
          style={{ userSelect: "none" }}>
          {text}
        </text>
      </g>
    );
  }
}

// ─── Level annotation (section drawings) ──────────────────────────────────────
export function LevelAnnotation({ x, y, label, side = "right" }) {
  const ts  = mmToPx(2.2);
  const len = mmToPx(8);
  const sw  = mmToPx(0.18);
  const dir = side === "right" ? 1 : -1;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Leader line */}
      <line x1={x} y1={y} x2={x + dir * len} y2={y} stroke="#000" strokeWidth={sw}/>
      {/* Level tick */}
      <line x1={x - mmToPx(2)} y1={y} x2={x + mmToPx(2)} y2={y} stroke="#000" strokeWidth={mmToPx(0.35)}/>
      <text x={x + dir * (len + mmToPx(1))} y={y + mmToPx(1)}
        textAnchor={side === "right" ? "start" : "end"}
        fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000"
        style={{ userSelect: "none" }}>
        {label}
      </text>
    </g>
  );
}

// ─── North arrow ──────────────────────────────────────────────────────────────
export function NorthArrow({ cx, cy, r, rotation = 0 }) {
  const R  = mmToPx(r);
  const sw = mmToPx(0.25);
  const ts = mmToPx(3);
  const angle = rotation * Math.PI / 180;
  const tip   = { x: cx + R * Math.sin(angle),      y: cy - R * Math.cos(angle) };
  const tail  = { x: cx - R * 0.6 * Math.sin(angle), y: cy + R * 0.6 * Math.cos(angle) };
  const leftW = { x: cx + R * 0.3 * Math.cos(angle), y: cy + R * 0.3 * Math.sin(angle) };
  const rightW= { x: cx - R * 0.3 * Math.cos(angle), y: cy - R * 0.3 * Math.sin(angle) };

  return (
    <g style={{ pointerEvents: "none" }}>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#000" strokeWidth={sw}/>
      {/* Filled half (north) */}
      <polygon points={`${tip.x},${tip.y} ${leftW.x},${leftW.y} ${tail.x},${tail.y}`}
        fill="#000"/>
      {/* Hollow half (south) */}
      <polygon points={`${tip.x},${tip.y} ${rightW.x},${rightW.y} ${tail.x},${tail.y}`}
        fill="none" stroke="#000" strokeWidth={sw}/>
      {/* N label */}
      <text x={tip.x} y={tip.y - mmToPx(3)}
        textAnchor="middle" fontSize={ts} fontWeight="700"
        fontFamily={DIM_STYLE.font} fill="#000"
        style={{ userSelect: "none" }}>N</text>
    </g>
  );
}

// ─── Scale bar ────────────────────────────────────────────────────────────────
export function ScaleBar({ x, y, scale, segments = 4 }) {
  // Each segment = 1000mm world = (1000/scale) drawing mm
  const segW  = mmToPx(1000 / scale);
  const barH  = mmToPx(2);
  const ts    = mmToPx(2.2);
  const sw    = mmToPx(0.25);
  const total = segW * segments;

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Baseline */}
      <line x1={x} y1={y + barH} x2={x + total} y2={y + barH} stroke="#000" strokeWidth={sw}/>
      {/* Alternating filled/empty segments */}
      {Array.from({ length: segments }).map((_, i) => (
        <rect key={i} x={x + i * segW} y={y} width={segW} height={barH}
          fill={i % 2 === 0 ? "#000" : "#fff"} stroke="#000" strokeWidth={sw}/>
      ))}
      {/* Tick labels */}
      {Array.from({ length: segments + 1 }).map((_, i) => (
        <g key={i}>
          <line x1={x + i * segW} y1={y} x2={x + i * segW} y2={y + barH + mmToPx(1)}
            stroke="#000" strokeWidth={sw}/>
          <text x={x + i * segW} y={y + barH + mmToPx(4)}
            textAnchor="middle" fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
            {i}m
          </text>
        </g>
      ))}
      {/* Scale label */}
      <text x={x + total / 2} y={y - mmToPx(2)}
        textAnchor="middle" fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        Scale 1:{scale}
      </text>
    </g>
  );
}

// ─── Door symbol (arc swing) ──────────────────────────────────────────────────
export function DoorSymbol({ x, y, width, angle, scale }) {
  const w = worldToPx(width, scale);
  const a = angle * Math.PI / 180;
  // Door leaf
  const doorX = x + w * Math.cos(a), doorY = y - w * Math.sin(a);
  // Arc path
  const arcR = w;
  const largeArc = a > Math.PI ? 1 : 0;
  const sw = mmToPx(LINE.VISIBLE.weight);
  return (
    <g style={{ pointerEvents: "none" }}>
      <line x1={x} y1={y} x2={doorX} y2={doorY} stroke="#000" strokeWidth={sw}/>
      <path d={`M ${x} ${y} A ${arcR} ${arcR} 0 0 1 ${doorX} ${doorY}`}
        fill="none" stroke="#000" strokeWidth={mmToPx(0.18)} strokeDasharray={`${mmToPx(3)} ${mmToPx(2)}`}/>
    </g>
  );
}

// ─── Window symbol (parallel lines) ──────────────────────────────────────────
export function WindowSymbol({ x1, y1, x2, y2, wallThick, scale }) {
  const t  = worldToPx(wallThick, scale);
  const dx = x2 - x1, dy = y2 - y1;
  const L  = Math.sqrt(dx*dx + dy*dy);
  if (L < 1) return null;
  const nx = -dy/L, ny = dx/L;
  const sw = mmToPx(LINE.VISIBLE.weight);
  const t3 = t/3;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Three parallel lines */}
      {[0, t3, t3*2].map((off, i) => (
        <line key={i}
          x1={x1 + nx*off} y1={y1 + ny*off}
          x2={x2 + nx*off} y2={y2 + ny*off}
          stroke="#000" strokeWidth={sw}/>
      ))}
    </g>
  );
}

// ─── Title block ──────────────────────────────────────────────────────────────
export function TitleBlock({ x, y, w, h, project, drawingTitle, drawingNo, scale, date, revision = "A" }) {
  const ts  = mmToPx(2.5);
  const ts2 = mmToPx(3.5);
  const ts3 = mmToPx(5);
  const sw  = mmToPx(0.35);

  const col1 = x + mmToPx(3);
  const col2 = x + w * 0.55;
  const row = (n) => y + mmToPx(n);

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Border */}
      <rect x={x} y={y} width={w} height={h} fill="#fff" stroke="#000" strokeWidth={sw}/>

      {/* Vertical dividers */}
      <line x1={x + w*0.5} y1={y} x2={x + w*0.5} y2={y + h} stroke="#000" strokeWidth={mmToPx(0.18)}/>
      <line x1={x + w*0.75} y1={y} x2={x + w*0.75} y2={y + h} stroke="#000" strokeWidth={mmToPx(0.18)}/>

      {/* Horizontal dividers */}
      <line x1={x} y1={y + h*0.4} x2={x + w} y2={y + h*0.4} stroke="#000" strokeWidth={mmToPx(0.18)}/>
      <line x1={x} y1={y + h*0.7} x2={x + w} y2={y + h*0.7} stroke="#000" strokeWidth={mmToPx(0.18)}/>

      {/* Project name */}
      <text x={col1} y={row(4)} fontSize={ts3} fontWeight="700" fontFamily={DIM_STYLE.font} fill="#000">
        {(project?.name || "Untitled Project").slice(0, 30)}
      </text>
      {/* Address */}
      <text x={col1} y={row(8)} fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        {(project?.address || "").slice(0, 40)}
      </text>
      {/* Client */}
      <text x={col1} y={row(11)} fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        Client: {project?.client || "—"}
      </text>

      {/* Drawing title */}
      <text x={col1} y={y + h*0.4 + mmToPx(5)} fontSize={ts2} fontWeight="700" fontFamily={DIM_STYLE.font} fill="#000">
        {drawingTitle}
      </text>

      {/* Scale / Date / Rev */}
      <text x={col1} y={y + h*0.7 + mmToPx(4)} fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        Scale 1:{scale}
      </text>
      <text x={x + w*0.5 + mmToPx(3)} y={y + h*0.7 + mmToPx(4)} fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        Date: {date || new Date().toLocaleDateString("en-IN")}
      </text>
      <text x={x + w*0.75 + mmToPx(3)} y={y + h*0.7 + mmToPx(4)} fontSize={ts} fontFamily={DIM_STYLE.font} fill="#000">
        Rev: {revision}
      </text>

      {/* Drawing number */}
      <text x={x + w*0.75 + mmToPx(3)} y={y + h - mmToPx(4)} fontSize={ts2} fontWeight="700" fontFamily={DIM_STYLE.font} fill="#000">
        Dwg No: {drawingNo}
      </text>

      {/* Drawn by / Dcode branding */}
      <text x={col1} y={y + h - mmToPx(1.5)} fontSize={mmToPx(1.8)} fontFamily={DIM_STYLE.font} fill="#888">
        Generated by J.A.D.E · DPL Homestar · dplhomestar.com · Design intent only — obtain licensed architect stamp before submission
      </text>
    </g>
  );
}
